import { useCallback, useState } from "react";
import { useDispatchApp, useSelectorApp } from "../lib/redux";
import { useTranslationApp } from "../lib/i18next";
import useAgencyIsAppSumo from "./useAgencyIsAppSumo";
import useAgencyFeatureAddons from "./useAgencyFeatureAddons";
import { startCreateFeatureAddonCheckout } from "../actions/featureAddons";
import { SimpleAlert } from "../components/Alerts/Alerts";
import {
  ALERT_ICON_TYPE_ERROR,
  ALERT_ICON_TYPE_SUCCESS,
} from "../utils/constants";
import {
  IN_A_FEW_MINUTES_YOUR_INVOICE_WILL_BE_AVAILABLE,
  UPGRADE_SUBSCRIPTION_SUCCESSFUL,
} from "../i18n/keysTranslations";
import { isFeatureAddonLimitsEligible } from "../utils/featureAddonCatalog";
import { errorCodeToText, hasKnownErrorTranslation } from "../utils/errors";

/**
 * Paywall helper for agencies eligible under the addon limits switch.
 * While that switch is off, every agency stays ungated (no paywall UI).
 * Entitlement comes from a live `agencies/{agencyID}/addons` listener.
 */
const useFeatureAddonPaywall = ({ addonKey }) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const { isAppSumo, isAppSumoResolved } = useAgencyIsAppSumo();
  const [open, setOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const planPrice = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo?.price
  );
  const eligible = isFeatureAddonLimitsEligible({
    isAppSumo,
    isAppSumoResolved,
    planPrice,
  });
  const { status, isLoading, refreshStatus } = useAgencyFeatureAddons({
    addonKey,
    enabled: eligible,
  });

  const openPaywall = useCallback(() => {
    if (!eligible) return;
    setOpen(true);
  }, [eligible]);

  const blockWhen = useCallback(
    async (isBlocked) => {
      if (!eligible) return false;
      const nextStatus = await refreshStatus();
      if (typeof isBlocked !== "function" || !isBlocked(nextStatus)) {
        return false;
      }
      setOpen(true);
      return true;
    },
    [eligible, refreshStatus]
  );

  const closePaywall = useCallback(() => setOpen(false), []);

  const checkoutTier = useCallback(
    async (tierId) => {
      if (!addonKey || !tierId) return { ok: false };
      setIsCheckingOut(true);
      const result = await dispatch(
        startCreateFeatureAddonCheckout({
          addonKey,
          tierId,
          origin: window.location.origin,
        })
      );
      setIsCheckingOut(false);
      if (result?.ok && result?.data?.upgraded) {
        SimpleAlert({
          title: t(UPGRADE_SUBSCRIPTION_SUCCESSFUL),
          text: t(IN_A_FEW_MINUTES_YOUR_INVOICE_WILL_BE_AVAILABLE),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
        setOpen(false);
        refreshStatus();
        return result;
      }
      if (result?.ok && result?.data?.paymentUrl) {
        window.location.assign(result.data.paymentUrl);
      } else if (!result?.ok) {
        const translated = hasKnownErrorTranslation(result?.code)
          ? errorCodeToText(result.code)
          : "";
        const title = translated || result?.message;
        if (title) {
          SimpleAlert({
            title,
            icon: ALERT_ICON_TYPE_ERROR,
          });
        }
      }
      return result;
    },
    [addonKey, dispatch, refreshStatus, t]
  );

  return {
    eligible,
    isAppSumoResolved,
    open,
    status,
    isLoading,
    isCheckingOut,
    openPaywall,
    blockWhen,
    closePaywall,
    refreshStatus,
    checkoutTier,
  };
};

export default useFeatureAddonPaywall;
